import type { Nullish } from '@iheartjane/types';

import { createUseQueryHook } from '../createUseQueryHook';
import type {
  FetchAeropayBankConnectionProps,
  FetchAeropayUserProps,
  FetchJanePayStoreUsagesProps,
} from './aeropay.api';
import {
  fetchAeropayBankConnection,
  fetchAeropayUser,
  fetchJanePayStoreUsages,
} from './aeropay.api';
import type {
  AeropayBankConnection,
  AeropayUser,
  Id,
  JanePayStoreUsages,
} from './aeropay.types';

export const AEROPAY_BASE_KEY = 'aeropay';

interface UseAeropayUserProps extends FetchAeropayUserProps {
  janeUserId: Nullish<Id>;
}

interface UseAeropayBankConnectionProps extends FetchAeropayBankConnectionProps {
  aeropayUserId?: Nullish<string>;
}

interface UseJanePayStoreUsagesProps extends FetchJanePayStoreUsagesProps {
  janeUserId: Nullish<Id>;
}

export const aeropayQueryKeys = {
  aeropayUser: ({ janeUserId }: Pick<UseAeropayUserProps, 'janeUserId'>) => [
    AEROPAY_BASE_KEY,
    'user',
    janeUserId,
  ],
  bankConnection: () => [AEROPAY_BASE_KEY, 'bankConnection'],
  janePayStoreUsages: ({
    janeUserId,
  }: Pick<UseJanePayStoreUsagesProps, 'janeUserId'>) => [
    AEROPAY_BASE_KEY,
    'janePayStoreUsages',
    janeUserId,
  ],
};

export const useAeropayUser = createUseQueryHook<
  UseAeropayUserProps,
  AeropayUser | null
>(
  fetchAeropayUser,
  aeropayQueryKeys.aeropayUser,
  ({ janeUserId, jwt }) => !!jwt && !!janeUserId
);

export const useAeropayBankConnection = createUseQueryHook<
  UseAeropayBankConnectionProps,
  AeropayBankConnection
>(
  fetchAeropayBankConnection,
  aeropayQueryKeys.bankConnection,
  ({ aeropayUserId, jwt }) => !!jwt && !!aeropayUserId
);

/**
 * Aeropay re-verifies a shopper the first time they pay at a given merchant, so
 * checkout needs to know whether this store is already one of theirs. Unresolved
 * or failed reads leave `data` undefined, which callers must read as "not yet
 * used here" — asking for an unnecessary code is the recoverable direction.
 */
export const useJanePayStoreUsages = createUseQueryHook<
  UseJanePayStoreUsagesProps,
  JanePayStoreUsages
>(
  fetchJanePayStoreUsages,
  aeropayQueryKeys.janePayStoreUsages,
  ({ janeUserId, jwt }) => !!jwt && !!janeUserId
);
